import { Component, computed, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { LayoutService } from '../../layouts/layout.service';
import { Incident, deriveStage, IncidentStage } from '../../shared/models/incident.models';
import { IncidentService } from '../../core/services/incident.service';
import { IncidentRecordComponent } from './incident-record/incident-record';
import { CanDeactivateComponent } from '../../auth/unsaved-changes.guard';
import { NewInvestigationModalComponent } from '../../shared/components/new-investigation-modal/new-investigation-modal';
import { AuthService } from '../../auth/auth.service';

const STAGE_LABELS: { id: IncidentStage; letter: string; name: string }[] = [
  { id: 'A', letter: 'A', name: 'Creation' },
  { id: 'B', letter: 'B', name: 'Supervisor Review' },
  { id: 'C', letter: 'C', name: 'Management Review' },
  { id: 'D', letter: 'D', name: 'Compliance & PQ' },
];

@Component({
  selector: 'app-incident-detail',
  imports: [NgClass, IncidentRecordComponent, NewInvestigationModalComponent],
  templateUrl: './incident-detail.html',
})
export class IncidentDetailComponent implements OnInit, CanDeactivateComponent {
  @ViewChild(IncidentRecordComponent) private record?: IncidentRecordComponent;

  private route = inject(ActivatedRoute);
  private layout = inject(LayoutService);
  private incidentService = inject(IncidentService);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  protected incidentId = '';
  protected incident = signal<Incident | null>(null);
  protected showUnsavedModal = signal(false);
  private deactivateSubject: Subject<boolean> | null = null;
  protected showInvestigationModal = signal(false);

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.record?.isDirty()) return true;
    this.showUnsavedModal.set(true);
    this.deactivateSubject = new Subject<boolean>();
    return this.deactivateSubject.asObservable();
  }

  protected confirmLeave(): void {
    this.showUnsavedModal.set(false);
    this.deactivateSubject?.next(true);
    this.deactivateSubject?.complete();
    this.deactivateSubject = null;
  }

  protected saveAndLeave(): void {
    this.showUnsavedModal.set(false);
    if (this.incidentId === 'new') {
      // Create mode: block original navigation, save handles its own navigation on success
      this.deactivateSubject?.next(false);
      this.deactivateSubject?.complete();
      this.deactivateSubject = null;
    }
    this.record?.save();
  }

  protected onSaved(): void {
    this.loadIncident();
    if (this.deactivateSubject) {
      this.deactivateSubject.next(true);
      this.deactivateSubject.complete();
      this.deactivateSubject = null;
    }
  }

  protected onSaveFailed(): void {
    if (this.deactivateSubject) {
      this.deactivateSubject.next(false);
      this.deactivateSubject.complete();
      this.deactivateSubject = null;
    }
  }

  protected readonly stageLabels = STAGE_LABELS;

  protected currentStage = computed(() => deriveStage(this.incident()));

  protected stageStatuses = computed(() => {
    const inc = this.incident();
    const stage = this.currentStage();
    return STAGE_LABELS.map((s) => {
      switch (s.id) {
        case 'A':
          return inc?.stageACompletionFlag
            ? 'completed'
            : stage === 'A' || stage === 'new'
              ? 'active'
              : 'pending';
        case 'B':
          return inc?.stageBCompletionFlag ? 'completed' : stage === 'B' ? 'active' : 'pending';
        case 'C':
          return inc?.stageCCompletionFlag || inc?.stageCApplicable === false
            ? 'completed'
            : stage === 'C'
              ? 'active'
              : 'pending';
        case 'D':
          return inc?.stageDPQCompletionFlag && inc?.stageDComplianceCompletionFlag
            ? 'completed'
            : stage === 'D'
              ? 'active'
              : 'pending';
        default:
          return 'pending' as const;
      }
    }) as Array<'completed' | 'active' | 'pending'>;
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.incidentId = params.get('id') ?? '';
      const isNew = this.incidentId === 'new';

      this.layout.set({
        title: isNew ? 'New Incident' : 'Incident',
        breadcrumb: 'Incident',
        backLabel: 'Return to List',
        switchPlaces: true,
        showBackButton: true,
      });

      if (!isNew) this.loadIncident();
    });
  }

  protected loadIncident(): void {
    this.incidentService
      .getIncidentById(this.incidentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inc) => {
          this.incident.set(inc);
          this.layout.set({
            title: inc.incidentNumber ? `${inc.incidentNumber}` : 'New Incident',
            title2: `Reported by: ${inc.createdByName}`,
            breadcrumb: 'Incident',
            backLabel: 'Return to List',
            switchPlaces: true,
            showBackButton: true,
            headerAction: this.auth.canDo('Investigations', ['Creation'])
              ? {
                  label: 'Investigation',
                  icon: 'search',
                  onClick: () => this.showInvestigationModal.set(true),
                }
              : undefined,
          });
        },
        error: () => this.incident.set(null),
      });
  }
}
